import * as Sentry from "@sentry/react";
export async function executeScripts(element: Element): Promise<void> {
    if (element instanceof HTMLScriptElement) {
        await cloneAndExecuteScript(element);
    } else {
        for (const script of Array.from(element.querySelectorAll("script"))) {
            await cloneAndExecuteScript(script);
        }
    }
}

/**
 * If you add a script by setting the innerHTML of an element, the script
 * doesn't execute {@see https://developer.mozilla.org/en-US/docs/Web/API/Element/innerHTML#security_considerations}.
 * This means we need to manually re-create the scripts using a `createElement("script")`
 * and replace them in the DOM for them to be actually executed.
 *
 * Further, since server side scripts are executed in order, we need to make sure
 * that we await the loading of each script before moving on to the next one.
 * This function executes the scripts and also waits for its execution.
 *
 * A third complication is that the scripts might depend on the DOMContentLoaded,
 * which has already fired. So we patch the addEventListener method to subscribe to
 * a custom event instead of the native DOMContentLoaded event before running the scripts.
 */
async function cloneAndExecuteScript(script: HTMLScriptElement): Promise<void> {
    let awaiter: Promise<unknown> = Promise.resolve();
    const newScript = cloneScript(script);
    if (newScript.src && !newScript.async) {
        awaiter = new Promise((resolve, reject) => {
            newScript.addEventListener("load", resolve);
            newScript.addEventListener("error", (ev) => reject(ev.error));
        });
    }
    try {
        // When the code contains a syntax error, `replaceChild` will not throw. Instead,
        // we'll get an asynchronous error event later.
        // To get around this, we run replaceChild in a sentry scope with a specific tag that is later used
        // to filter out errors caused by this.
        Sentry.withScope((scope) => {
            scope.setTag("softr.custom-code", true);
            script.parentNode?.replaceChild(newScript, script);
        });
    } catch (e) {
        console.error("Custom code error", e);
    }

    if (script.src && !newScript.async) {
        try {
            await awaiter;
        } catch (e) {
            // The default behaviour of the browser is to log the error
            // in case of script loading failures.
            // That's why we're catching this instead of bubbling it up.
            // We don't want a bad script tag to prevent the loading of
            // the rest of the blocks.
            console.error("Custom code error", e);
        }
    }
    return;
}

function cloneScript(script: HTMLScriptElement) {
    const newScript = document.createElement("script");
    for (const attr of Array.from(script.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
    }
    // By default, these attributes aren't copied over in the above loop 🤷
    newScript.async = script.async;
    newScript.defer = script.defer;
    if (!script.src) {
        // Adding a sourceURL makes it possible to distinguish errors in custom code from
        // errors in our code.
        newScript.innerHTML = script.innerHTML + "\n//# sourceURL=softr-custom-code";
    }
    return newScript;
}
